import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Wallet, ArrowRight, KeyRound } from "lucide-react";

export default function ChoosePath() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const linkCompany = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/link-company", { code: code.trim().toUpperCase() });
      toast.success(`Linked to ${data.company_name}`);
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not link");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid-bg">
      <div className="max-w-3xl mx-auto p-4 md:p-8 lg:p-12">
        {/* header */}
        <div className="mb-8">
          <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-3">Welcome {user?.name?.split(" ")[0]}</div>
          <h1 className="font-heading text-4xl md:text-6xl">CHOOSE YOUR PATH</h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-base">
            You&apos;re signed in but not yet linked to a company.
          </p>
        </div>

        {/* Option A */}
        <div className="brutal-card p-6 md:p-8 mb-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-safety-yellow text-black px-4 h-10 flex items-center font-black uppercase tracking-widest text-xs">
            Option A
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-14 w-14 bg-safety-yellow text-black border-2 border-black flex items-center justify-center">
              <Building2 className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl leading-none">JOINING A CREW?</h2>
              <div className="text-sm font-bold uppercase tracking-wider text-muted-foreground mt-1">
                Your admin will share a one-time access code.
              </div>
            </div>
          </div>

          <form onSubmit={linkCompany} className="mt-5">
            <label className="block text-xs font-bold uppercase tracking-wider mb-2">Company Access Code</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <KeyRound className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input data-testid="link-code-input" value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="industrial-input pl-11 uppercase tracking-[0.2em] font-black"
                  placeholder="e.g. KARM2026" />
              </div>
              <button type="submit" data-testid="link-code-submit" disabled={loading || !code.trim()}
                className="btn-primary sm:w-auto w-full">
                {loading ? "Linking..." : (<>Link to Company <ArrowRight className="w-5 h-5 ml-2" strokeWidth={3} /></>)}
              </button>
            </div>
            <div className="text-xs text-muted-foreground mt-3">
              Hint: Try <code className="font-mono font-bold text-safety-yellow">KARM2026</code> for the demo org.
            </div>
          </form>
        </div>

        {/* divider */}
        <div className="flex items-center gap-4 my-4">
          <div className="flex-1 border-t-2 border-dashed border-foreground/30 dark:border-safety-yellow/40" />
          <span className="font-heading text-xl text-muted-foreground">OR</span>
          <div className="flex-1 border-t-2 border-dashed border-foreground/30 dark:border-safety-yellow/40" />
        </div>

        {/* Option B */}
        <div className="brutal-card p-6 md:p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-foreground text-background px-4 h-10 flex items-center font-black uppercase tracking-widest text-xs">
            Option B
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-14 w-14 bg-foreground text-background border-2 border-foreground dark:border-safety-yellow flex items-center justify-center">
              <Wallet className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl leading-none">INDEPENDENT OR PRIVATE?</h2>
              <div className="text-sm font-bold uppercase tracking-wider text-muted-foreground mt-1">
                Keep a personal vault — link a company later, anytime.
              </div>
            </div>
          </div>

          <div className="mt-5">
            <button data-testid="continue-vault" onClick={() => navigate("/vault")}
              className="btn-outline w-full">
              Continue to My Certifications <ArrowRight className="w-5 h-5 ml-2" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
